/**
 * Box 4 (spec §4.2, §5.3), Contribution · ROI · mROAS, through D1's OverviewCard and OverviewTable:
 * - each channel over the version's window from the bundle's summary: spend, contribution, share of the outcome and
 *   ROI as the trainer wrote them; mROAS at the current budget with its 90% range (marginalAtCurrent);
 * - rows by spend, highest first, stopped channels last (plan R15), then "Media รวม", and the baseline's share below;
 * - a channel marginalAtCurrent leaves out (stopped) reads "—", never 0; a ratio over a total that prints 0.00 is
 *   "—" (T9a), so nothing reads Infinity or NaN.
 * A count outcome (spec §5.3's other columns) names its unit and prints counts; ROI and mROAS become R14's costs per
 * unit, spend ÷ contribution and 1 ÷ mROAS, each ranged, "มากกว่า ฿X" where the range has no top. Its rows go by the
 * next unit's cost, cheapest first, unbounded then stopped last (R15), and only หยุดวิ่ง has a chip.
 *
 * @module components/budget/ContributionTable
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { OverviewCard } from '@/components/overview/OverviewBox';
import { OverviewTable, type OverviewColumn } from '@/components/overview/OverviewTable';
import { PlatformDot } from '@/components/PlatformDot';
import type { Bundle, Ranged } from '@/services/budget/bundleSchema';
import {
  costOf, costRange, costRangeText, costText, isCount, unitName, unitsName, type CostRange, type CountMetric,
} from '@/services/budget/outcomeUnits';
import { MIN_MOVE } from '@/services/budget/simulator';
import { fmtRoas, type TFn } from '@/services/channels/channelFormat';
import { DASH, fmtCount, fmtMoney, fmtPct } from '@/services/overview/overviewFormat';
import { platformShortLabel } from '@/utils/platformLabel';

/** One more baht returns one baht here: คุ้มที่ขอบ from this mean mROAS up (spec §5.3). */
export const BREAK_EVEN = 1;

const CHIP = 'inline-block whitespace-nowrap rounded-full border px-1.5 text-[11px] print:whitespace-normal';
const CHIP_CLASS = {
  good: `${CHIP} border-success-700 text-success-700`,
  below: `${CHIP} border-warning-700 text-warning-700`,
  stopped: `${CHIP} border-neutral-400 text-neutral-800`,
} as const;
type Status = keyof typeof CHIP_CLASS;

interface Figures {
  spend: number;
  /** null: the total spend prints 0.00. */
  spendShare: number | null;
  contribution: number;
  /** Of the total outcome (the bundle's share). */
  share: number;
  roi: number | null;
}

interface ChannelRow extends Figures {
  channel: string;
  stopped: boolean;
  /** null: not in marginal, which marginalAtCurrent gives running channels only. */
  marginal: Ranged | null;
  /** A count's costs per unit (R14): spend ÷ contribution, and 1 ÷ marginal for the next unit (null without one). */
  avgCost: CostRange;
  nextCost: CostRange | null;
}

interface Totals extends Figures {
  /** A count's Σ spend ÷ Σ contribution, which has no range. */
  cost: number | null;
}

/** A table line: a channel, or "Media รวม" (OverviewTable has no footer, so the total is the last line). */
type Line = { kind: 'row'; row: ChannelRow } | { kind: 'total'; total: Totals };
type Order = (x: ChannelRow, y: ChannelRow) => number;

const sum = (xs: readonly number[]): number => xs.reduce((s, x) => s + x, 0);

/** A ratio over a total that prints 0.00 is not measurable (T9a): "—", never Infinity. */
const ratio = (n: number, d: number): number | null => (d >= MIN_MOVE ? n / d : null);

/** R15 for revenue: by spend, highest first; stopped channels last. sort is stable, so a tie keeps the bundle's order. */
const byRevenueOrder: Order = (x, y) => Number(x.stopped) - Number(y.stopped) || y.spend - x.spend;

/** The figure a count row shows for its next unit: the mean, or an unbounded range's floor; null reads "—". */
const shownCost = (c: CostRange | null): number | null => (c === null ? null : c.unbounded ? c.lo : c.mean);
/** A bounded cost, then an unbounded one, then none. */
const costRank = (c: CostRange | null): number => (c === null ? 2 : Number(c.unbounded));
const ascending = (a: number | null, b: number | null): number =>
  (a === null || b === null ? Number(a === null) - Number(b === null) : a - b);

/** R15 for a count: by ต้นทุนต่อ <unit> ถัดไป, cheapest first, unbounded last; stopped channels last. */
const byCostOrder: Order = (x, y) => Number(x.stopped) - Number(y.stopped)
  || costRank(x.nextCost) - costRank(y.nextCost) || ascending(shownCost(x.nextCost), shownCost(y.nextCost));

const linesOf = (b: Bundle, marginal: Readonly<Record<string, Ranged>>, order: Order): Line[] => {
  const spend = sum(b.channels.map((c) => b.summary[c.id].spend));
  const rows = b.channels.map((c): ChannelRow => {
    const s = b.summary[c.id];
    const m: Ranged | undefined = marginal[c.id];
    return {
      channel: c.id, stopped: c.stopped, spend: s.spend, spendShare: ratio(s.spend, spend), contribution: s.contribution.mean,
      share: s.share.mean, roi: s.roi.mean, marginal: m ?? null,
      avgCost: costRange(s.spend, s.contribution), nextCost: m === undefined ? null : costRange(1, m),
    };
  });
  // The means add up (the range of a sum is not the sum of ranges, so the total has none).
  const contribution = sum(rows.map((r) => r.contribution));
  const total: Totals = {
    spend, spendShare: ratio(spend, spend), contribution, share: sum(rows.map((r) => r.share)), roi: ratio(contribution, spend),
    cost: costOf(spend, contribution),
  };
  return [...[...rows].sort(order).map((row): Line => ({ kind: 'row', row })), { kind: 'total', total }];
};

/**
 * Two decimals, rounded down (T12h's rule): a mean under 1 never prints 1.00 beside ต่ำกว่าทุน, and lo ≤ mean ≤ hi
 * still prints in order.
 */
export const down2 = (x: number): string => {
  const near = Number(x.toFixed(2));
  return (near > x ? near - 0.01 : near).toFixed(2);
};

const Marginal: React.FC<{ value: Ranged | null }> = ({ value }) => (value === null
  ? <>{DASH}</>
  : <>{down2(value.mean)} <span className="text-xs text-neutral-700">({down2(value.lo)}–{down2(value.hi)})</span></>);

const statusOf = (r: ChannelRow): Status | null => {
  if (r.stopped) return 'stopped';
  if (r.marginal === null) return null;
  return r.marginal.mean >= BREAK_EVEN ? 'good' : 'below';
};

/** Literal keys, so the locale integrity test sees them. */
const statusText = (s: Status, t: TFn): string => {
  if (s === 'stopped') return t('budget.stopped');
  return s === 'good' ? t('budget.contribution.good') : t('budget.contribution.below');
};

const Pill: React.FC<{ status: Status }> = ({ status }) => {
  const { t } = useTranslation();
  return <span data-status={status} className={CHIP_CLASS[status]}>{statusText(status, t)}</span>;
};

const StatusChip: React.FC<{ row: ChannelRow }> = ({ row }) => {
  const s = statusOf(row);
  return s === null ? <>{DASH}</> : <Pill status={s} />;
};

/** A cost per unit with its range, or "มากกว่า ฿X" alone where the range has no top (R14). */
const Cost: React.FC<{ value: CostRange | null; currency: string }> = ({ value, currency }) => {
  const { t } = useTranslation();
  if (value === null) return <>{DASH}</>;
  const money = (n: number): string => fmtMoney(n, currency);
  const text = costText(value, money, t);
  return value.unbounded
    ? <>{text}</>
    : <>{text} <span className="text-xs text-neutral-700">({costRangeText(value, money, t)})</span></>;
};

const figuresOf = (l: Line): Figures => (l.kind === 'row' ? l.row : l.total);
const bold = (on: boolean, node: React.ReactNode): React.ReactNode => (on ? <span className="font-semibold">{node}</span> : node);

const num = (id: string, header: string, text: (f: Figures) => string): OverviewColumn<Line> => ({
  id, header, numeric: true, cell: (l) => bold(l.kind === 'total', text(figuresOf(l))),
});

/** Channel · Spend · Share of spend, which both column sets open with. */
const spendColumns = (t: TFn, currency: string): Array<OverviewColumn<Line>> => [
  {
    id: 'channel', header: t('budget.contribution.col.channel'),
    cell: (l) => (l.kind === 'row'
      ? <span className="inline-flex items-center gap-1.5"><PlatformDot platform={l.row.channel} />{platformShortLabel(l.row.channel)}</span>
      : <span className="font-semibold">{t('budget.contribution.media')}</span>),
  },
  num('spend', t('budget.contribution.col.spend'), (f) => fmtMoney(f.spend, currency)),
  num('spendShare', t('budget.contribution.col.spendShare'), (f) => fmtPct(f.spendShare)),
];

/** Spec §5.3's revenue column set, in the prototype's order. */
const revenueColumns = (t: TFn, currency: string): Array<OverviewColumn<Line>> => [
  ...spendColumns(t, currency),
  num('contribution', t('budget.contribution.col.contribution'), (f) => fmtMoney(f.contribution, currency)),
  num('share', t('budget.contribution.col.share'), (f) => fmtPct(f.share)),
  num('roi', t('budget.contribution.col.roi'), (f) => fmtRoas(f.roi)),
  // The total has no mROAS or status: blank, as BreakdownTables leaves a column with nothing to add up.
  {
    id: 'mroas', header: t('budget.contribution.col.mroas'), numeric: true,
    cell: (l) => (l.kind === 'row' ? <Marginal value={l.row.marginal} /> : null),
  },
  { id: 'status', header: t('budget.contribution.col.status'), cell: (l) => (l.kind === 'row' ? <StatusChip row={l.row} /> : null) },
];

/** Spec §5.3's count column set: units, and costs per unit where lower is better; no break-even chip, as a unit has no value. */
const countColumns = (t: TFn, currency: string, metric: CountMetric): Array<OverviewColumn<Line>> => {
  const unit = unitName(metric, t);
  const units = unitsName(metric, t);
  return [
    ...spendColumns(t, currency),
    num('contribution', t('budget.contribution.col.contributionUnits', { units }), (f) => fmtCount(f.contribution)),
    num('share', t('budget.contribution.col.shareUnits', { units }), (f) => fmtPct(f.share)),
    {
      id: 'avgCost', header: t('budget.contribution.col.avgCost', { unit }), numeric: true,
      cell: (l) => (l.kind === 'row' ? <Cost value={l.row.avgCost} currency={currency} /> : bold(true, fmtMoney(l.total.cost, currency))),
    },
    {
      id: 'nextCost', header: t('budget.contribution.col.nextCost', { unit }), numeric: true,
      cell: (l) => (l.kind === 'row' ? <Cost value={l.row.nextCost} currency={currency} /> : null),
    },
    { id: 'status', header: t('budget.contribution.col.status'), cell: (l) => (l.kind === 'row' && l.row.stopped ? <Pill status="stopped" /> : null) },
  ];
};

interface Copy {
  title: string;
  subtitle: string;
  baseline: string;
  columns: Array<OverviewColumn<Line>>;
  order: Order;
}

/** The box's words, columns and row order for the version's outcome: revenue, or a count's unit. */
const copyOf = (b: Bundle, t: TFn): Copy => {
  const m = b.outcome_metric;
  const n = b.window.weeks;
  const pct = fmtPct(b.baseline.share.mean);
  if (!isCount(m)) {
    return {
      title: t('budget.contribution.title'), subtitle: t('budget.contribution.subtitle', { n }),
      baseline: t('budget.contribution.baselineShare', { pct }), columns: revenueColumns(t, b.currency), order: byRevenueOrder,
    };
  }
  const unit = unitName(m, t);
  return {
    title: t('budget.contribution.titleCount', { unit }), subtitle: t('budget.contribution.subtitleCount', { n, unit }),
    baseline: t('budget.contribution.baselineShareCount', { pct, units: unitsName(m, t) }),
    columns: countColumns(t, b.currency, m), order: byCostOrder,
  };
};

interface Props {
  bundle: Bundle;
  /** marginalAtCurrent(bundle), which box 7 shares. */
  marginal: Readonly<Record<string, Ranged>>;
}

export const ContributionTable: React.FC<Props> = ({ bundle, marginal }) => {
  const { t } = useTranslation();
  const copy = copyOf(bundle, t);
  return (
    <OverviewCard id="budget-4" title={copy.title}>
      <p className="-mt-2 mb-2 text-xs text-neutral-700">{copy.subtitle}</p>
      <OverviewTable
        columns={copy.columns} rows={linesOf(bundle, marginal, copy.order)}
        rowKey={(l) => (l.kind === 'row' ? `row:${l.row.channel}` : 'total')}
      />
      <p data-baseline-share className="mt-2 text-xs text-neutral-700">{copy.baseline}</p>
    </OverviewCard>
  );
};
